import { useMutation } from '@apollo/client';
import { useCallback, useState } from 'react';
import {
  type RemoveItemFromTemplateMutation,
  type RemoveItemFromTemplateMutationVariables,
} from 'graphql-query-contracts';
import { removeItemFromTemplateMutation } from 'graphql-queries';

import { RemoveItemFromTemplateError } from './errors/RemoveItemFromTemplateError';

export type RemoveItemFromTemplateParams = {
  channelId: string;
  templateId: string;
  itemId: string;
};

export type RemoveItemFromTemplateResult = {
  success: boolean;
  newVersion: number;
};

export type UseRemoveItemFromTemplateResult = {
  removeItemFromTemplate: (
    params: RemoveItemFromTemplateParams
  ) => Promise<RemoveItemFromTemplateResult>;
  loading: boolean;
  error: Error | undefined;
};

function validateParams(params: RemoveItemFromTemplateParams): void {
  if (!params.channelId?.trim()) {
    throw new RemoveItemFromTemplateError('channelId is required');
  }

  if (!params.templateId?.trim()) {
    throw new RemoveItemFromTemplateError('templateId is required');
  }

  if (!params.itemId?.trim()) {
    throw new RemoveItemFromTemplateError('itemId is required');
  }
}

export const useRemoveItemFromTemplate =
  (): UseRemoveItemFromTemplateResult => {
    const [removeItem, { loading }] = useMutation<
      RemoveItemFromTemplateMutation,
      RemoveItemFromTemplateMutationVariables
    >(removeItemFromTemplateMutation);

    const [error, setError] = useState<Error | undefined>(undefined);

    const removeItemFromTemplate = useCallback(
      async (
        params: RemoveItemFromTemplateParams
      ): Promise<RemoveItemFromTemplateResult> => {
        try {
          setError(undefined);

          validateParams(params);

          const variables: RemoveItemFromTemplateMutationVariables = {
            input: {
              channelId: params.channelId,
              templateId: params.templateId,
              itemId: params.itemId,
            },
          };

          const mutationResult = await removeItem({ variables });

          const payload = mutationResult.data?.removeItemFromTemplate;

          if (!payload) {
            throw new RemoveItemFromTemplateError();
          }

          if (!payload.success) {
            throw new RemoveItemFromTemplateError(
              'Remove item operation did not succeed'
            );
          }

          return {
            success: payload.success,
            newVersion: payload.newVersion,
          };
        } catch (err) {
          const wrappedError =
            err instanceof RemoveItemFromTemplateError
              ? err
              : new RemoveItemFromTemplateError(
                  err instanceof Error
                    ? err.message
                    : 'Failed to remove item from inspection template',
                  { cause: err }
                );

          setError(wrappedError);

          throw wrappedError;
        }
      },
      [removeItem]
    );

    return {
      removeItemFromTemplate,
      loading,
      error,
    };
  };
